// Human readable one-liners for audit log entries (Napló page).
// Every action gets a summary built from its details; unknown actions still get
// a readable label + their most useful fields instead of a generic "unknown".

const REFRESH_KINDS = {
    news: 'hírek',
    shops: 'üzletek',
    offers: 'ajánlatok',
    mapsvgs: 'térképek',
    services: 'szolgáltatások',
};

const name = (d) => [d.lastname, d.firstname].filter(Boolean).join(' ');
const who = (d) =>
    [name(d), d.email && `<${d.email}>`].filter(Boolean).join(' ') || (d.id ? `#${d.id}` : '');
const huf = (v) => `${new Intl.NumberFormat('hu-HU').format(Number(v) || 0)} Ft`;
const count = (v) => (Array.isArray(v) ? v.length : v);

const FORMATTERS = {
    login_request: (d) => `Belépési kód kérése: ${d.email || '?'}`,
    login_success: (d) => `Sikeres belépés (felhasználó #${d.user_id || '?'})`,
    login_failed: (d) =>
        `Sikertelen belépés${
            d.reason === 'user_not_found'
                ? ' – a felhasználó nem létezik'
                : ' – hibás vagy lejárt kód'
        }`,
    registration: (d) => `Regisztráció: ${d.email || '?'}`,
    mailchimp_subscribe: (d) => `Hírlevél feliratkozás: ${d.email || '?'}`,
    delete_request: (d) => `Fiók törlési kérelem: ${d.email || `#${d.user_id || '?'}`}`,
    user_create: (d) => `Felhasználó létrehozva: ${who(d)}${d.role === 'admin' ? ' (admin)' : ''}`,
    user_update: (d) => `Felhasználó módosítva: ${who(d)}`,
    user_delete: (d) => `Felhasználó törölve: ${who(d)}`,
    favorites_add: (d) => `Kedvenc hozzáadva (üzlet #${d.shopid})`,
    favorites_remove: (d) => `Kedvenc eltávolítva (üzlet #${d.shopid})`,
    refresh_data_start: () => 'Tartalom frissítés elindult',
    refresh_data_end: () => 'Tartalom frissítés befejeződött',
    refresh_result: (d) => {
        const kind = Object.keys(REFRESH_KINDS).find((k) => k in d);
        return kind
            ? `Tartalom frissítve: ${REFRESH_KINDS[kind]} (${d.lang || '?'}) – ${count(d[kind])} db`
            : 'Tartalom frissítve';
    },
    parking_ticket_info: (d) => {
        const t = d.data || {};
        const parts = [
            t.status,
            t.amount !== undefined && huf(t.amount),
            Number(t.paid) ? 'fizetve' : null,
        ];
        return `Parkolójegy frissítve: ${d.ticketid}${
            parts.some(Boolean) ? ` – ${parts.filter(Boolean).join(', ')}` : ''
        }`;
    },
    parking_ticket_pay: (d) =>
        `Parkolójegy fizetés a parkoló rendszerben: ${d.ticketid}${
            count(d.discounts) ? ` (${count(d.discounts)} kedvezmény)` : ''
        }`,
    parking_ticket_error: (d) =>
        `Parkolójegy hiba: ${d.ticketid} – ${d.error || 'ismeretlen hiba'}`,
    parking_ticket_discount: (d) =>
        `Kedvezmény beváltva az appban: ${d.ticketid} (${d.count || 1}. kedvezmény)`,
    parking_payment_amount_mismatch: (d) =>
        `Parkolójegy kifizetve, de a díj közben nőtt: ${d.ticketid} – fizetve ${huf(
            d.paid
        )}, esedékes ${huf(d.due)} (${d.orderref})`,
    parking_ticket_close: (d) =>
        `Parkolás kézzel lezárva${d.paid ? ' és kifizetve (kiengedve)' : ''}: ${d.ticketid} – „${
            d.comment || ''
        }”`,
};

// "some_new_action" -> "Some new action"
const humanize = (action) => {
    const s = String(action || 'esemény')
        .replace(/[_-]+/g, ' ')
        .trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
};

// most telling scalar fields of an unknown entry, e.g. "email: a@b.hu, ticketid: ABC123"
const FALLBACK_KEYS = ['email', 'ticketid', 'id', 'user_id', 'status', 'error', 'comment'];
function summarizeDetails(details) {
    if (!details || typeof details !== 'object') return details ? String(details) : '';
    const keys = [...FALLBACK_KEYS.filter((k) => k in details), ...Object.keys(details)];
    return [...new Set(keys)]
        .filter(
            (k) =>
                ['string', 'number', 'boolean'].includes(typeof details[k]) &&
                !/token|pass|secret/i.test(k)
        )
        .slice(0, 3)
        .map((k) => `${k}: ${details[k]}`)
        .join(', ');
}

export default function formatAuditLog(action, details) {
    const d = details && typeof details === 'object' ? details : {};
    const fmt = FORMATTERS[action];
    if (fmt) {
        try {
            return fmt(d);
        } catch (e) {
            // malformed details, fall through to the generic summary
        }
    }
    const extra = summarizeDetails(details);
    return extra ? `${humanize(action)} – ${extra}` : humanize(action);
}
